/**
 * EvidenceModal - Display evidence events for a control signal
 *
 * Shows events that triggered a control failure/warning with actionable insights
 */
import React, { useState, useEffect } from 'react';
import {
  Modal,
  Table,
  Tag,
  Button,
  Alert,
  Spin,
  Descriptions,
  Space,
  Divider
} from 'antd';
import {
  EyeOutlined,
  FileTextOutlined,
  WarningOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { getEventDetail } from '../api/trustgridApi';
import { showError } from '../../../utils/notifications';
import EventDetailModal from './EventDetailModal';

function EvidenceModal({ visible, onClose, signal, controlData }) {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedEventId, setSelectedEventId] = useState(null);
  const [eventDetailVisible, setEventDetailVisible] = useState(false);

  useEffect(() => {
    if (visible && signal && signal.evidence_refs) {
      fetchEvidence();
    }
  }, [visible, signal]);

  const fetchEvidence = async () => {
    setLoading(true);
    try {
      // Fetch first 100 events from evidence_refs
      const eventIds = signal.evidence_refs.slice(0, 100);
      const eventPromises = eventIds.map(id => getEventDetail(id).catch(() => null));
      const eventResponses = await Promise.all(eventPromises);

      const fetchedEvents = eventResponses
        .filter(r => r && r.data)
        .map(r => r.data);

      setEvents(fetchedEvents);
    } catch (error) {
      console.error('Failed to fetch evidence events:', error);
      showError(error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'fail': return 'red';
      case 'warning': return 'orange';
      case 'pass': return 'green';
      default: return 'default';
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'critical': return 'red';
      case 'high': return 'orange';
      case 'medium': return 'gold';
      case 'low': return 'blue';
      default: return 'default';
    }
  };

  const columns = [
    {
      title: 'Event ID',
      dataIndex: 'id',
      key: 'id',
      width: 90
    },
    {
      title: 'Timestamp',
      dataIndex: 'ts',
      key: 'ts',
      width: 180,
      render: (ts) => new Date(ts).toLocaleString()
    },
    {
      title: 'Connector',
      key: 'connector',
      width: 180,
      render: (_, record) => (
        record.connector ? (
          <div>
            <div style={{ fontWeight: 500 }}>{record.connector.name}</div>
            <Tag size="small" color="cyan">{record.connector.type}</Tag>
          </div>
        ) : (
          <span style={{ color: '#999' }}>N/A</span>
        )
      )
    },
    {
      title: 'Provider',
      dataIndex: 'provider',
      key: 'provider',
      width: 120,
      render: (provider) => <Tag color="blue">{provider}</Tag>
    },
    {
      title: 'Event Type',
      dataIndex: 'event_type',
      key: 'event_type',
      width: 150,
      render: (type) => <Tag>{type}</Tag>
    },
    {
      title: 'PII Status',
      key: 'pii_status',
      width: 120,
      render: (_, record) => (
        record.pii_detected ? (
          <Tag color="red" icon={<WarningOutlined />}>
            PII Detected
          </Tag>
        ) : (
          <Tag color="green">Clean</Tag>
        )
      )
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 100,
      fixed: 'right',
      render: (_, record) => (
        <Button
          size="small"
          icon={<EyeOutlined />}
          onClick={() => {
            setSelectedEventId(record.id);
            setEventDetailVisible(true);
          }}
        >
          View
        </Button>
      )
    }
  ];

  return (
    <>
      <Modal
        title={
          <Space>
            <WarningOutlined style={{ color: getStatusColor(signal?.status) }} />
            <span>Evidence: {signal?.control_code} - {controlData?.name || 'Control Details'}</span>
          </Space>
        }
        open={visible}
        onCancel={onClose}
        width={1200}
        footer={[
          <Button
            key="control"
            icon={<FileTextOutlined />}
            onClick={() => {
              navigate(`/trustgrid/controls/${signal?.control_code}`);
              onClose();
            }}
          >
            View Implementation Guide
          </Button>,
          <Button key="close" type="primary" onClick={onClose}>
            Close
          </Button>
        ]}
      >
        {/* Control Status Summary */}
        <Alert
          message={`Control Status: ${signal?.status?.toUpperCase()}`}
          description={
            <div>
              <Descriptions size="small" column={2}>
                <Descriptions.Item label="Confidence">
                  {signal?.confidence}%
                </Descriptions.Item>
                <Descriptions.Item label="Evidence Count">
                  {signal?.evidence_count} events
                </Descriptions.Item>
                <Descriptions.Item label="Severity" span={2}>
                  <Tag color={getSeverityColor(controlData?.severity)}>
                    {controlData?.severity?.toUpperCase() || 'MEDIUM'}
                  </Tag>
                </Descriptions.Item>
              </Descriptions>
              {signal?.rationale && (
                <div style={{ marginTop: 12 }}>
                  <strong>Rationale:</strong>
                  <p style={{ marginTop: 4, whiteSpace: 'pre-wrap' }}>
                    {signal.rationale.substring(0, 300)}
                    {signal.rationale.length > 300 && '...'}
                  </p>
                </div>
              )}
            </div>
          }
          type={signal?.status === 'fail' ? 'error' : signal?.status === 'warning' ? 'warning' : 'info'}
          showIcon
          style={{ marginBottom: 16 }}
        />

        {/* Remediation Hint */}
        {signal?.status === 'fail' && (
          <Alert
            message="Remediation Required"
            description={
              <div>
                <p><strong>Recommended Actions:</strong></p>
                <ul style={{ marginTop: 8, marginBottom: 0 }}>
                  <li>Review the {signal.evidence_count} events below</li>
                  <li>Identify root cause of non-compliance</li>
                  <li>Implement fixes per the control guide</li>
                  <li>Re-run scan to verify remediation</li>
                </ul>
              </div>
            }
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
          />
        )}

        <Divider>Evidence Events ({events.length} of {signal?.evidence_count || 0})</Divider>

        {/* Events Table */}
        <Spin spinning={loading}>
          {events.length === 0 && !loading ? (
            <div style={{ textAlign: 'center', padding: 40, color: '#888' }}>
              No evidence events available
            </div>
          ) : (
            <Table
              dataSource={events}
              columns={columns}
              rowKey="id"
              pagination={{ pageSize: 10, showSizeChanger: false }}
              scroll={{ x: 900 }}
              size="small"
            />
          )}
        </Spin>

        {signal?.evidence_count > 100 && (
          <Alert
            message={`Showing first 100 events. Total evidence: ${signal.evidence_count} events`}
            type="info"
            showIcon
            style={{ marginTop: 12 }}
          />
        )}
      </Modal>

      {/* Event Detail Modal */}
      {selectedEventId && (
        <EventDetailModal
          visible={eventDetailVisible}
          onClose={() => {
            setEventDetailVisible(false);
            setSelectedEventId(null);
          }}
          eventId={selectedEventId}
        />
      )}
    </>
  );
}

export default EvidenceModal;
